import type { entityVersions } from '@clockwise/clients';
import { NO_CUSTOMER_ACTIONS } from '@clockwise/clients/customers';
import { ApiError } from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  ApplyChangeDialog,
  savedMessage,
  shouldAsk,
} from '@/components/custom/apply-change-dialog';
import { BackLink } from '@/components/custom/back-link';
import { CustomFieldsEditor } from '@/components/custom/custom-fields-editor';
import {
  blankCustomerValues,
  CustomerFormFields,
  CustomerFormSchema,
  type CustomerFormValues,
  customerFormToPatch,
  customerToFormValues,
  isCustomerFormField,
  parentCustomerStandIn,
} from '@/components/custom/customer-form-fields';
import {
  effectiveRange,
  useEntityVersions,
  withVersionValues,
} from '@/components/custom/entity-version-history';
import { NoAccess } from '@/components/custom/no-access';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { customersApi } from '@/lib/api';
import { isForbidden } from '@/lib/errors';
import { lastSearch } from '@/lib/last-search';
import { queryKeys } from '@/lib/query-keys';
import { useObjectCustomFields } from '@/lib/use-object-custom-fields';

/**
 * `?version=N` turns this page into a HISTORICAL AMEND of that version rather
 * than an edit of the record (D10). Same form, same fields, same validation —
 * seeded from what the version says and submitted to the amend endpoint.
 *
 * A separate dialog was the obvious alternative and is the wrong one: it would
 * have re-implemented the field set, the custom-fields editor, the field-level
 * API error mapping and the unsaved-changes guard, and then drifted from them.
 * Editing a version IS editing this form; only where the values come from and
 * where they go changes.
 */
const editSearch = z.object({
  version: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute('/_authenticated/customers/$id/edit')({
  component: CustomerEditPage,
  validateSearch: editSearch,
});

function CustomerEditPage() {
  const { id } = Route.useParams();
  const { version: amendingVersion } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: queryKeys.customers.detail(id),
    queryFn: () => customersApi.get(id),
  });
  const customer = detail.data?.data ?? null;
  // `customer.admin` in THIS customer's unit — the API's answer, with the
  // check its amend and "always" writes run.
  const canAmend = (detail.data?.related.actions ?? NO_CUSTOMER_ACTIONS).admin;

  // Genuinely only fetched when amending — this endpoint diffs every version
  // and resolves FK labels server-side, so an ordinary edit must not pay for it.
  const versionsQuery = useEntityVersions('customer', id, {
    enabled: amendingVersion != null,
  });
  const amending =
    amendingVersion != null
      ? (versionsQuery.data?.data.find((v) => v.version === amendingVersion) ?? null)
      : null;
  // THE VERSION IS NOT OPTIONAL WHEN AMENDING. Until it resolves, the form
  // below would hold the LIVE record's values while the submit path writes them
  // to a past version — today's data silently becoming what a closed period
  // reports. So the form is not rendered at all until this is settled, and a
  // failed or missing version says so instead of offering an editable form.
  const amendNotReady = amendingVersion != null && !amending;

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(CustomerFormSchema),
    defaultValues: blankCustomerValues,
  });

  // Seeded from the VERSION when amending. `customFields` is a versioned
  // column and rides along in every patch body, so seeding it from the live
  // record would overwrite whatever that version recorded the moment anyone
  // amends any other field — a silent edit to a column nobody touched.
  const cf = useObjectCustomFields(
    'customer',
    amending
      ? (amending.values.customFields as NonNullable<typeof customer>['customFields'] | undefined)
      : customer?.customFields,
  );

  useEffect(() => {
    if (!customer) return;
    // Never seed from the live record while an amend is pending resolution —
    // see `amendNotReady`.
    if (amendNotReady) return;
    // Amending seeds from the VERSION's snapshot merged over the live record,
    // exactly as the read-only as-of view does — so the form opens showing what
    // that version says, not what the record says today.
    form.reset(
      customerToFormValues(amending ? withVersionValues(customer, amending, () => ({})) : customer),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, customer?.updatedAt, amending?.version]);

  /** An edit waiting on its answer to "When did this become true?". */
  const [question, setQuestion] = useState<{
    values: CustomerFormValues;
    impact: entityVersions.EditImpact;
  } | null>(null);
  const [checking, setChecking] = useState(false);

  // Shared by the save and its preview: the preview runs the save's own
  // checks, so a field error from either lands on the same field.
  const handleSaveError = (err: unknown) => {
    setQuestion(null);
    const hadCfErrors = cf.handleApiError(err);
    if (err instanceof ApiError && err.fields?.length) {
      for (const fe of err.fields) {
        if (fe.path.startsWith('customFields.')) continue;
        if (isCustomerFormField(fe.path)) {
          form.setError(fe.path as keyof CustomerFormValues, { message: fe.message });
        }
      }
      toast.error('Please fix the highlighted fields');
      return;
    }
    if (hadCfErrors) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    toast.error(err instanceof Error ? err.message : 'Save failed');
  };

  const mutation = useMutation({
    mutationFn: async ({
      values,
      applyTo,
    }: {
      values: CustomerFormValues;
      applyTo?: entityVersions.ApplyTo;
      impact?: entityVersions.EditImpact;
    }) => {
      if (!customer) throw new Error('Customer not loaded');
      const payload = customerFormToPatch(values, cf.values);
      if (amendingVersion != null) {
        return customersApi.amendVersion(customer.id, amendingVersion, payload);
      }
      return customersApi.patch(customer.id, payload, applyTo);
    },
    onSuccess: (result, vars) => {
      setQuestion(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      if (amendingVersion == null) {
        toast.success(
          vars.impact
            ? savedMessage(
                vars.impact,
                vars.applyTo ?? 'today',
                (result as { meta?: entityVersions.PatchMeta }).meta?.restated,
              )
            : 'Customer updated',
        );
        navigate({ to: '/customers/$id', params: { id } });
        return;
      }
      // An amend touches ONE version and never ripples, so the versions that
      // still carry the replaced value are named rather than quietly rewritten.
      // A toast, not a second write — the operator stays the one deciding.
      const later =
        (result as { data?: { laterVersionsStillHolding?: { version: number }[] } }).data
          ?.laterVersionsStillHolding ?? [];
      toast.success(
        later.length === 0
          ? 'Correction saved'
          : later.length === 1
            ? 'Correction saved. 1 later period still shows the old value. Open it to change that too.'
            : `Correction saved. ${later.length} later periods still show the old value. Open each to change it too.`,
        later.length > 0 ? { duration: 10000 } : undefined,
      );
      navigate({ to: '/customers/$id', params: { id }, search: { version: amendingVersion } });
    },
    onError: (err) => handleSaveError(err),
  });

  const onSubmit = form.handleSubmit(async (v) => {
    cf.reset();
    // Only an edit that changes what logged timesheets report is asked
    // "When did this become true?" — and only of someone allowed to answer
    // "always". Everyone else's edit applies from today, as it always has.
    if (amendingVersion != null || !canAmend || !customer) {
      mutation.mutate({ values: v });
      return;
    }
    setChecking(true);
    try {
      const { data: impact } = await customersApi.previewPatch(
        customer.id,
        customerFormToPatch(v, cf.values),
      );
      if (shouldAsk(impact)) {
        setQuestion({ values: v, impact });
        return;
      }
    } catch (err) {
      handleSaveError(err);
      return;
    } finally {
      setChecking(false);
    }
    mutation.mutate({ values: v });
  });

  // Amending history is an admin-tier authority, separate from `customer.edit`:
  // editing the record changes it from today, amending a version changes what
  // every report over that period has always said. The server enforces the same
  // gate; this keeps someone from filling in a form that can only 403.
  // Judged once the customer has loaded: before that nothing is known.
  if (amendingVersion != null && detail.data && !canAmend) {
    return (
      <PageShell>
        <NoAccess permission="customer.admin" resource="this record's history" />
      </PageShell>
    );
  }

  // 403 on load → this customer isn't in the caller's assigned set.
  if (detail.isError && isForbidden(detail.error)) {
    return (
      <PageShell>
        <NoAccess permission="customer.edit" resource="this customer" />
      </PageShell>
    );
  }

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/customers/$id" params={{ id }}>
          Back to customer
        </BackLink>
      </div>

      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">
          {detail.isLoading ? (
            <Skeleton className="h-7 w-56" />
          ) : amendingVersion != null ? (
            `Correct past details · ${customer?.name ?? ''}`
          ) : (
            `Edit ${customer?.name ?? ''}`
          )}
        </h1>
      </header>

      {/* Amending is not editing, and the page has to say so before someone
          types into it: these values are what a past period reports, and saving
          restates every report over that period rather than changing the record
          from today. */}
      {amending ? (
        <div className="max-w-2xl rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm dark:bg-amber-950/30">
          <p>
            You're changing what was true for {customer?.name ?? 'this customer'} during{' '}
            <strong>{effectiveRange(amending)}</strong>. Every report covering those dates will show
            the new values.
          </p>
          <p className="mt-1 text-muted-foreground">
            {customer?.name ?? 'This customer'}'s current details and later periods keep their
            values.
          </p>
        </div>
      ) : null}

      {detail.isError ? (
        <p className="text-sm text-destructive">
          {detail.error instanceof ApiError ? detail.error.message : 'Failed to load customer.'}
        </p>
      ) : versionsQuery.isError ? (
        <p className="text-sm text-destructive">
          Could not load this record's history, so there is nothing safe to correct. Reload the page
          to try again.
        </p>
      ) : amendingVersion != null && !versionsQuery.isLoading && !amending ? (
        <p className="text-sm text-destructive">This period no longer exists on this record.</p>
      ) : detail.isLoading || amendNotReady ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : customer ? (
        <form onSubmit={onSubmit} className="flex flex-col gap-6">
          <div className="max-w-2xl">
            <CustomerFormFields form={form} parentCustomer={parentCustomerStandIn(customer)} />
          </div>

          <div className="max-w-2xl">
            <CustomFieldsEditor {...cf.EditorProps} disabled={mutation.isPending} />
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={mutation.isPending || checking}>
              {mutation.isPending || checking
                ? 'Saving…'
                : amendingVersion != null
                  ? 'Save correction'
                  : 'Save'}
            </Button>
            <Button type="button" variant="outline" disabled={mutation.isPending} asChild>
              <Link
                to="/customers/$id"
                params={{ id }}
                search={lastSearch('/customers/$id', { params: { id } })}
              >
                Cancel
              </Link>
            </Button>
          </div>
        </form>
      ) : null}

      <ApplyChangeDialog
        open={question !== null}
        subject={customer?.name ?? ''}
        subjectName={customer?.name ?? ''}
        impact={question?.impact ?? null}
        pending={mutation.isPending}
        onCancel={() => setQuestion(null)}
        onConfirm={(applyTo) =>
          question && mutation.mutate({ values: question.values, applyTo, impact: question.impact })
        }
      />
    </PageShell>
  );
}
